import {Drawer, Typography} from '@mui/material';
import {ReactElement, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import SavedSearchPanel, {
  Flow_Type,
  SavedSearchDetails,
} from './SavedSearchPanel';
import AurButton from '../common/aur-button/AurButton';
import AurSearchBox from '../common/aur-search-box/AurSearchBox';
import {t} from 'i18next';
import {MIN_SEARCH_CHAR} from '../../constants/constants';
import {QueryFieldHeaderDrawer} from '../pages/index-search/query-field/components/QueryFieldHeaderDrawer';
import {extractSavedSearchId, isEmpty} from '../../utils/utils';
import {ExtendedTreeItemProps} from '../eca-doc-list/eca-save-search/EcaSaveSearch';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {RelationalGroup} from '../pages/index-search/RelationshipGroupPopup';
import {useAnalytics} from '../../hooks/useAnalytics';
import {RootState} from '../../redux/store';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../constants/analyticsConstants';

export interface SavedSearchType {
  id: string;
  label: string;
  ecaSearchId?: string;
  directory_structure_ids?: string;
  lastRunDetail?: {
    lastRunUserId: string;
    lastRunUserName: string;
    lastRunTime: string;
  };
  relationalGroupList?: RelationalGroup[];
}
interface Props {
  title?: string;
  searchPlaceholder?: string;
  showDrawer: boolean;
  onSelectSavedSearch?: (data: SavedSearchType) => void;
  selectedIndex?: string;
  onCloseDrawer: () => void;
  disablePortal?: boolean; // Use it for jest test case only
  showQueryFieldHeader?: boolean;
  showNotes?: boolean;
  indexNameLabel?: string;
  expandedIds?: string[];
  selectedSearch?: TreeViewBaseItem<ExtendedTreeItemProps> | null;
  flowType?: Flow_Type;
  isUsedInQueryBuilder?: boolean;
  /**
   * Use to clear saved search when its clear from parent.
   * When flyout again open then apply button should be disabled as last picked save search already cleared
   */
  triggerResetPickedSearch?: boolean;
}

const ECASavedSearchPanelSideDrawer: React.FC<Props> = ({
  title = t('eca.savedSearchSelectionDialog.title'),
  searchPlaceholder = t('eca.savedSearchSelectionDialog.searchSavedSearches'),
  showDrawer,
  onSelectSavedSearch,
  selectedIndex,
  onCloseDrawer,
  disablePortal,
  showQueryFieldHeader = false,
  showNotes = true,
  indexNameLabel,
  expandedIds,
  selectedSearch,
  flowType = Flow_Type.OTHER,
  isUsedInQueryBuilder = false,
  triggerResetPickedSearch,
}) => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedSavedSearch, setSelectedSavedSearch] =
    useState<SavedSearchType | null>(null);
  const [selectedSearchDetailUI, setSelectedSearchDetailUI] =
    useState<ReactElement | null>(null);

  const [isDetailsLoaded, setIsDetailsLoaded] = useState<boolean>(false);
  const savedSearchRequiresRun = useSelector(
    (state: RootState) => state.ecaAdhocSearch.savedSearchLastRun
  );
  const {trackGAEvent} = useAnalytics();

  useEffect(() => {
    if (!showDrawer) setSelectedSearchDetailUI(null);
  }, [showDrawer]);

  useEffect(() => {
    if (triggerResetPickedSearch) setSelectedSavedSearch(null);
  }, [triggerResetPickedSearch]);

  const handleApplyClick = () => {
    if (onSelectSavedSearch && selectedSavedSearch) {
      onSelectSavedSearch(selectedSavedSearch);
    }
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.apply'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.str.savedSearchOkAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.selecteSavedSearch'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  };

  const handleOnFetchSavedSearch = (savedSearchDetails: SavedSearchDetails) => {
    if (
      savedSearchDetails?.id &&
      selectedSavedSearch?.id &&
      extractSavedSearchId(savedSearchDetails?.id ?? '') ===
        extractSavedSearchId(selectedSavedSearch?.id ?? '')
    ) {
      setSelectedSavedSearch({
        ...selectedSavedSearch,
        ecaSearchId: savedSearchDetails.ecaSearchId,
        lastRunDetail: savedSearchDetails.lastRunDetail,
        relationalGroupList: savedSearchDetails?.relationalGroupList ?? [],
      });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={showDrawer}
      PaperProps={{
        className: 'saved-search-side-panel-drawer',
        component: 'aside',
        'aria-label': title,
        'aria-labelledby': 'saved-search-drawer-title',
      }}
      onClose={onCloseDrawer}
      disablePortal={disablePortal}
    >
      {showQueryFieldHeader ? (
        <QueryFieldHeaderDrawer
          title={title}
          isApplyDisabled={
            (!isUsedInQueryBuilder && isEmpty(selectedSavedSearch)) ||
            (isUsedInQueryBuilder && !isDetailsLoaded)
          }
          onCancelClicked={onCloseDrawer}
          onApplyClicked={handleApplyClick}
        />
      ) : (
        <div className="panel-header">
          <Typography className="typo-text headline2_size title-label" id="saved-search-drawer-title">
            {title}
          </Typography>
          <div className="button-row">
            <AurButton
              type="exit"
              size="Medium"
              label={t('common.cancel')}
              onClick={onCloseDrawer}
              dataTestId="cancel-button"
            />
            <AurButton
              type="continue"
              size="Medium"
              label={t('buttons.apply')}
              disabled={
                isEmpty(selectedSavedSearch) || savedSearchRequiresRun
              }
              onClick={handleApplyClick}
              dataTestId="apply-button"
              classNames="button-apply"
            />
          </div>
        </div>
      )}
      <div className="search-box-container">
        <AurSearchBox
          label={searchPlaceholder}
          parentClassName="search-field-input-div"
          classNameProps="text-field"
          minCharForSearch={MIN_SEARCH_CHAR}
          onSearch={setSearchInput}
          dataTestId="search-input"
        />
      </div>
      <div className="content-container">
        <div className="panel-container">
          <div className="left-search-list-tree">
            <SavedSearchPanel
              selectedIndex={selectedIndex ?? ''}
              enableFlayout={false}
              renderSelectedSearchDetail={setSelectedSearchDetailUI}
              onPickSavedSearch={setSelectedSavedSearch}
              onFetchSavedSearch={handleOnFetchSavedSearch}
              openForPickSavedSearch
              enableSearch={false}
              searchInput={searchInput}
              showNotes={showNotes}
              indexNameLabel={indexNameLabel}
              expandedIds={expandedIds}
              selectedSearch={selectedSearch}
              handleSearchId={true}
              flowType={flowType}
              onLoadedDetails={setIsDetailsLoaded}
            />
          </div>
          <div className="right-search-detail">
            {!isEmpty(selectedSavedSearch) && selectedSearchDetailUI}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ECASavedSearchPanelSideDrawer;
